import {
  Card,
  Divider,
  HorizontalScrollView,
  List,
  ListItem,
  PageHeaderWrapper,
  Text,
} from "@clipboard-health/ui-components";
import { CardContent, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { openUrlInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { DocumentPageScrollWrapper } from "@src/appV2/redesign/Documents/Details/ScrollWrapper";
import { useRequirementDetailsContext } from "@src/appV2/redesign/Documents/Details/useRequirementDetailsContext";
import { DOCUMENT_DETAILS_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { Fragment } from "react";
import { generatePath, Redirect } from "react-router-dom";

import { AcceptedTbTestTypes } from "./components/AcceptedTbTestTypes";
import { SchedulingProviderCard } from "./components/SchedulingProviderCard";
import {
  isScheduleTbTestSource,
  isTbTestGuidedUploadRequirement,
  SCHEDULE_TB_TEST_DISCLAIMER,
  TB_TEST_SCHEDULING_PROVIDERS,
  TB_TEST_UPLOAD_OPTIONS,
} from "./constants";

export function ScheduleTbTestPage() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const { requirementId } = workerRequirement;
  const { showErrorToast } = useToast();
  const { source: sourceSearchParameter } = useSearchParams<{ source?: string }>();
  const source = isScheduleTbTestSource(sourceSearchParameter) ? sourceSearchParameter : undefined;

  const isTbTestRequirement = isTbTestGuidedUploadRequirement(workerRequirement);

  useLogEffect(
    APP_V2_USER_EVENTS.VIEWED_SCHEDULE_TB_TEST_PAGE,
    {
      requirementId,
      source,
    },
    { enabled: isTbTestRequirement }
  );

  // This shouldn't happen in normal navigation, but if someone tries to open
  // this page for a requirement that isn't a TB Test, redirect back to details.
  if (!isTbTestRequirement) {
    return <Redirect to={generatePath(DOCUMENT_DETAILS_FULL_PATH, { requirementId })} />;
  }

  return (
    <PageWrapper variant="quaternary">
      <PageHeaderWrapper variant="quaternary">
        <PageHeaderTopRow />
      </PageHeaderWrapper>

      <DocumentPageScrollWrapper spacing={9} sx={{ paddingBottom: 10 }}>
        <Stack spacing={4} sx={{ textAlign: "center" }}>
          <Text variant="h4" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            Schedule a TB Test
          </Text>
          <Text
            sx={(theme) => ({
              color: theme.palette.text.secondary,
            })}
          >
            {SCHEDULE_TB_TEST_DISCLAIMER}
          </Text>
        </Stack>

        <Stack spacing={5}>
          <Stack spacing={1}>
            <Text bold variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              Where to get your TB test
            </Text>
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
              You can schedule a test at a local urgent care or clinic.
            </Text>
          </Stack>
          <HorizontalScrollView stretch>
            {TB_TEST_SCHEDULING_PROVIDERS.map((provider) => (
              <SchedulingProviderCard
                key={provider.name}
                name={provider.name}
                description={provider.description}
                buttonLabel={provider.buttonLabel}
                onSchedule={async () => {
                  logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_SCHEDULE_PROVIDER_CLICKED, {
                    requirementId,
                    providerId: provider.id,
                    source,
                  });
                  try {
                    await openUrlInBrowser(provider.url, "schedule-tb-test");
                  } catch {
                    showErrorToast("Unable to open the scheduling page. Please try again.");
                  }
                }}
              />
            ))}
          </HorizontalScrollView>
        </Stack>

        <Stack spacing={5}>
          <Text bold variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            You may satisfy with one of these tests
          </Text>
          <AcceptedTbTestTypes />
        </Stack>

        <Stack spacing={5}>
          <Text bold variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            Pick one of the following options to upload
          </Text>
          <Card variant="tertiary" sx={{ paddingY: 1 }}>
            <CardContent>
              <Stack spacing={6}>
                {TB_TEST_UPLOAD_OPTIONS.map((option, index) => (
                  <Fragment key={option.title}>
                    {index > 0 && <Divider />}
                    <Stack spacing={3}>
                      <Text variant="body1">{option.title}</Text>
                      <Text variant="body2" sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        {option.description}
                      </Text>
                      {"notes" in option && (
                        <List dense disablePadding sx={{ listStyleType: "disc", paddingLeft: 5 }}>
                          {option.notes.map((note) => (
                            <ListItem key={note} sx={{ display: "list-item", padding: 0 }}>
                              <Text
                                variant="body2"
                                sx={{ color: (theme) => theme.palette.text.tertiary }}
                              >
                                {note}
                              </Text>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Stack>
                  </Fragment>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </DocumentPageScrollWrapper>
    </PageWrapper>
  );
}
